import React, { useState } from 'react';
import { Box } from '@mui/material';
import axios from 'axios';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import colors from 'src/utils/colors';
import { EyeShow, EyeHide } from 'src/components/SvgIcons/SvgIcons';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { useKeywordsDisplayStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/stores';
import { useAppSelector, useSnackbar } from 'src/utils/Hooks';
import { showContentSyndication } from 'src/utils/app';

type LibraryReviewHeaderProps = {
  handleExport?: () => void;
  isExporting?: boolean;
  // Syndication props
  retailerId?: number;
  retailerSku?: string;
  contentGenId?: string;
};

export const LibraryReviewHeader: React.FC<LibraryReviewHeaderProps> = ({
  handleExport,
  isExporting,
  retailerId,
  retailerSku,
  contentGenId
}) => {
  const { showKeywords, setShowKeywords } = useKeywordsDisplayStore();
  const { showSnackbar } = useSnackbar();
  const { targetUrl } = useAppSelector((state) => state.app);

  const [isSyndicating, setIsSyndicating] = useState(false);

  const handleToggleKeywords = () => {
    setShowKeywords(!showKeywords);
  };

  const handleSyndicate = async () => {
    if (!retailerId || !retailerSku || !contentGenId) {
      showSnackbar({ type: 'error', message: 'Missing required data for syndication' });
      return;
    }

    setIsSyndicating(true);
    try {
      const response = await axios.post(`https://${targetUrl}/api/syndication/syndicate`, {
        retailerId,
        retailerSku,
        contentGenId,
        provider: 'SALSIFY'
      });

      if (response.data?.success) {
        showSnackbar({ type: 'success', message: 'Content syndication started successfully' });
      } else {
        showSnackbar({ type: 'error', message: response.data?.error || 'Failed to start syndication' });
      }
    } catch (error) {
      console.error('Syndication error:', error);
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || axiosError.message || 'Failed to syndicate content';
      showSnackbar({ type: 'error', message: errorMessage });
    } finally {
      setIsSyndicating(false);
    }
  };

  const canSyndicate = !!retailerId && !!retailerSku && !!contentGenId;
  const isSyndicationEnabled = showContentSyndication();

  return (
    <Box sx={{ mb: '32px', position: 'relative' }}>
      {/* Header with action buttons */}
      <Flex
        alignItems="flex-start"
        justifyContent="space-between"
        gap={2}
        sx={{
          mb: 1,
          overflow: 'visible',
          height: '20px'
        }}
      >
        <Box>
          <Text
            variant="h3"
            fontWeight={700}
            sx={{
              color: colors.darkBlue,
              fontSize: '20px',
              lineHeight: '28px'
            }}
          >
            Content Review
          </Text>
        </Box>

        {/* Action buttons positioned on the right */}
        <Flex gap={16} alignItems="center">
          <SlButton
            startIcon={
              showKeywords ? (
                <EyeHide style={{ width: 16, height: 16, marginRight: 8 }} />
              ) : (
                <EyeShow style={{ width: 16, height: 16, marginRight: 8 }} />
              )
            }
            variant="normal"
            onClick={handleToggleKeywords}
            buttonSx={{
              paddingLeft: '8px',
              paddingRight: '12px'
            }}
          >
            <Text variant="body2" sx={{ color: colors.darkBlue, fontWeight: 500, fontSize: '14px' }}>
              {showKeywords ? 'Hide keywords' : 'Show keywords'}
            </Text>
          </SlButton>
          <SlButton variant="normal" onClick={() => handleExport?.()} disabled={isExporting || !handleExport}>
            {isExporting ? 'Exporting...' : 'Export'}
          </SlButton>
          {isSyndicationEnabled && (
            <SlButton
              variant="contained"
              onClick={handleSyndicate}
              disabled={isSyndicating || !canSyndicate}
            >
              {isSyndicating ? 'Syndicating...' : 'Syndicate to Salsify'}
            </SlButton>
          )}
        </Flex>
      </Flex>

      {/* Subtext */}
      <Box sx={{ pr: 10 }}>
        <Text
          sx={{
            color: colors.darkBlue,
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '24px'
          }}
        >
          Review and compare all content versions for this product.
        </Text>
      </Box>
    </Box>
  );
};
